import React, { useState, useEffect, useRef, useCallback } from 'react';
// Remove useLocation import
import styles from '../../pages/CustomCompany/CompanyDetails.module.scss';

const CompanyDetailsUpload = ({ empId: propEmpId, adminId: propAdminId, onClose, onSuccess }) => {
  // State declarations
  const [noError, setNoError] = useState(0);
  const [emailCd, setEmailCd] = useState('');
  const [empIdCd, setEmpIdCd] = useState('');
  const [mobileNum, setMobileNum] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [contactNum, setContactNum] = useState('');
  const [cityListArr, setCityListArr] = useState([]);
  const [cities, setCities] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [areas, setAreas] = useState([]);
  const [userLocationDetails, setUserLocationDetails] = useState({});
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState('./images1/Company-Logo.png');
  const [uploadButtonText, setUploadButtonText] = useState('Upload');
  
  // Form field states
  const [employerId, setEmployerId] = useState('');
  const [industry, setIndustry] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const [cityName, setCityName] = useState('');
  const [areaName, setAreaName] = useState('');
  const [addressText, setAddressText] = useState('');
  const [contactName, setContactName] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [numOfEmployees, setNumOfEmployees] = useState('');
  const [yearFound, setYearFound] = useState('');
  const [gstNum, setGstNum] = useState('');
  const [referalOption, setReferalOption] = useState('');

  const fileInputRef = useRef(null);

  // Use props directly - no need for useLocation
  const empId = propEmpId;
  const adminId = propAdminId;

  // Environment configuration
  const releaseEnvironmentProd = true;

  // Base URL functions
  const getBaseUrl = useCallback(() => {
    return releaseEnvironmentProd 
      ? 'https://prod.java.taizo.in/webEmployer'
      : 'https://dev.java.taizo.in/webEmployer';
  }, [releaseEnvironmentProd]);

  const getAdminBaseUrl = useCallback(() => {
    return releaseEnvironmentProd
      ? 'https://prod.java.taizo.in/admin'
      : 'https://dev.java.taizo.in/admin';
  }, [releaseEnvironmentProd]);

  const getNodejsBaseUrl = useCallback(() => {
    return releaseEnvironmentProd
      ? 'https://prod.node.taizo.in'
      : 'https://dev.node.taizo.in';
  }, [releaseEnvironmentProd]);

  // Utility functions
  const toTitleCase = (phrase) => {
    return phrase
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .trim();
  };

  const toTitleCase1 = (phrase) => {
    return phrase
      .split(/(\W+)/)
      .map((part) => {
        if (part.match(/[a-zA-Z]/)) {
          return part.charAt(0).toUpperCase() + part.slice(1);
        }
        return part;
      })
      .join('');
  };

  const validateEmail = (email) => {
    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    return emailRegex.test(email);
  };

  const validateGst = (gst) => {
    const testGst = gst.toUpperCase();
    return /\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}/.test(testGst);
  };

  // Load employer details on component mount or when empId changes
  useEffect(() => {
    if (empId) {
      uploadCompanyDetails(empId);
    }
  }, [empId]);

  // Load industries on component mount
  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const response = await fetch(`${getBaseUrl()}/jobIndustries`, {
          method: 'GET',
        });
        const data = await response.json();
        if (data.data && data.data.length > 0) {
          setIndustries(data.data);
        }
      } catch (error) {
        console.error('Error fetching industries:', error);
      }
    };

    fetchIndustries();
  }, [getBaseUrl]);

  // Load cities on component mount
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch(`${getBaseUrl()}/cities?state_id=0`, {
          method: 'GET',
        });
        const data = await response.json();
        setCities(data.data);
        const cityNames = data.data.map(city => city.city);
        setCityListArr(cityNames);
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    };

    fetchCities();
  }, [getBaseUrl]);

  // Handle city change to load areas
  useEffect(() => {
    if (cityName) {
      const matchedCity = cities.find(
        city => city.city.toUpperCase() === cityName.toUpperCase()
      );
      if (matchedCity) {
        fetchAreas(matchedCity.id);
      }
    }
  }, [cityName, cities]);

  const fetchAreas = async (cityId) => {
    try {
      const response = await fetch(`${getBaseUrl()}/areas?city_id=${cityId}`, {
        method: 'GET',
      });
      const data = await response.json();
      setAreas(data.data);
    } catch (error) {
      console.error('Error fetching areas:', error);
    }
  };

  // Generate year options
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i > 1899; i--) {
      years.push(i);
    }
    return years;
  };

  // Handle file upload
  const handleLogoClick = () => {
    fileInputRef.current.click();
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedExtensions = /(\.jpg|\.jpeg|\.png)$/i;
      if (!allowedExtensions.exec(file.name)) {
        alert('Invalid file type');
        return;
      }
      
      setCompanyLogo(file);
      setLogoPreview(URL.createObjectURL(file));
      setUploadButtonText('Change');
      
      removeError('companyImg');
    }
  };

  // Google Maps Autocomplete
  useEffect(() => {
    const initializeMap = () => {
      if (window.google && window.google.maps) {
        const input = document.getElementById('companyName');
        const autocomplete = new window.google.maps.places.Autocomplete(input);
        
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          const latitude = place.geometry.location.lat();
          const longitude = place.geometry.location.lng();
          
          setAddressText(place.formatted_address);
          setCompanyName(place.name);
          
          let state, pin_code, country, area;
          
          place.address_components.forEach(component => {
            if (component.types.includes('locality')) {
              setCityName(component.long_name);
            } else if (component.types.includes('administrative_area_level_1')) {
              state = component.long_name;
            } else if (component.types.includes('postal_code')) {
              pin_code = component.long_name;
            } else if (component.types.includes('country')) {
              country = component.long_name;
            } else if (component.types.includes('sublocality_level_1')) {
              area = component.long_name;
            }
          });
          
          setUserLocationDetails({
            latitude,
            longitude,
            state,
            pin_code,
            country,
            area,
          });
          
          removeError('addressText');
        });
      }
    };

    if (window.google) {
      initializeMap();
    }
  }, []);

  // Validation function
  const employerValidationFN = () => {
    let errorCount = 0;
    
    if (!employerId) {
      addError('employerId1');
      errorCount++;
    } else {
      removeError('employerId1');
    }
    
    if (!businessType) {
      addError('business_type');
      errorCount++;
    } else {
      removeError('business_type');
    }
    
    if (!industry) {
      addError('industry');
      errorCount++;
    } else {
      removeError('industry');
    }
    
    if (!companyName) {
      addError('companyName');
      errorCount++;
    } else {
      removeError('companyName');
    }
    
    if (!cityName) {
      addError('cityName');
      errorCount++;
    } else {
      if (cityListArr.indexOf(cityName) !== -1) {
        removeError('cityName');
      } else {
        const cityErrText = document.querySelector(`.${styles.city_err_text}`);
        if (cityErrText) {
          cityErrText.textContent = 'Result not found';
        }
        addError('cityName');
        setTimeout(() => setCityName(''), 2000);
      }
    }
    
    // if (!emailValue) {
    //   const emailError = document.querySelector(`.${styles.emailError}`);
    //   if (emailError) {
    //     emailError.textContent = 'Please enter your email id';
    //   }
    //   addError('emailValue');
    //   errorCount++;
    // } else {
    //   if (!validateEmail(emailValue)) {
    //     const emailError = document.querySelector(`.${styles.emailError}`);
    //     if (emailError) {
    //       emailError.textContent = 'Please enter a valid email address';
    //     }
    //     addError('emailValue');
    //     errorCount++;
    //   } else {
    //     removeError('emailValue');
    //   }
    // }

    if (!emailValue) {
  addError('emailValue');
  errorCount++;
} else {
  if (!validateEmail(emailValue)) {
    addError('emailValue');
    errorCount++;
  } else {
    removeError('emailValue');
  }
}
    
    if (!areaName) {
      addError('areaName');
      errorCount++;
    } else {
      removeError('areaName');
    }
    
    if (!addressText) {
      addError('addressText');
      errorCount++;
    } else {
      removeError('addressText');
    }
    
    if (!contactName) {
      addError('contactName');
      errorCount++;
    } else {
      removeError('contactName');
    }
    
    if (!whatsappNumber) {
      addError('whatapp_number');
      errorCount++;
    } else if (whatsappNumber.length > 0 && whatsappNumber.length < 10) {
      addError('whatapp_number');
      errorCount++;
    } else {
      removeError('whatapp_number');
    }
    
    if (!mobileNum) {
      addError('mobileNum');
      errorCount++;
    } else if (mobileNum.length > 0 && mobileNum.length < 10) {
      addError('mobileNum');
      errorCount++;
    } else {
      removeError('mobileNum');
    }
    
    if (!numOfEmployees) {
      addError('numOfEmployes');
      errorCount++;
    } else {
      removeError('numOfEmployes');
    }
    
    if (!yearFound) {
      addError('yearFound');
      errorCount++;
    } else {
      removeError('yearFound');
    }
    
    if (!gstNum) {
      addError('gstNum');
      errorCount++;
    } else {
      if (validateGst(gstNum)) {
        removeError('gstNum');
      } else {
        addError('gstNum');
        errorCount++;
      }
    }
    
    if (!referalOption) {
      addError('referalOption');
      errorCount++;
    } else {
      removeError('referalOption');
    }
    
    setNoError(errorCount);
    return errorCount;
  };

  const addError = (idName) => {
    const element = document.getElementById(idName);
    if (element && element.parentElement) {
      element.parentElement.classList.add(styles.error);
    }
  };

  const removeError = (idName) => {
    const element = document.getElementById(idName);
    if (element && element.parentElement) {
      element.parentElement.classList.remove(styles.error);
    }
  };

  // Handle input changes to remove errors
  const handleInputChange = (e, setter) => {
    setter(e.target.value);
    removeError(e.target.id);
  };

  const handleSelectChange = (e, setter) => {
    setter(e.target.value);
    removeError(e.target.id);
  };

  // Upload company details
  const uploadCompanyDetails = async (empId) => {
    if (!empId) return;
    
    try {
      const response = await fetch(`${getBaseUrl()}/empDetails?emp_id=${empId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      if (data && data.code === 200 && data.status === 'success' && data.employer) {
        const employer = data.employer;
        const categoryValue = employer.category ? employer.category.trim() : '';

        setLogoPreview(employer.companyLogo || './images1/Company-Logo.png');
        setIndustry(employer.industry || '');
        setEmployerId(employer.id || '');
        setBusinessType(categoryValue);
        setCompanyName(employer.companyName || '');
        setEmailValue(employer.emailId || '');
        setCityName(employer.city || '');
        setAreaName(employer.area || '');
        setAddressText(employer.address || '');
        setGstNum(employer.regProofNumber || '');
        setYearFound(employer.yearFounded || '');
        setContactName(employer.contactPersonName || '');
        setNumOfEmployees(employer.noOfEmployees || '');
        setWebsiteUrl(employer.websiteUrl || '');
        setWhatsappNumber(employer.whatsappNumber || '');
        setMobileNum(employer.mobileNumber || '');
        setContactNum(employer.phone || '');
        setReferalOption(employer.reference || '');
      }
    } catch (error) {
      console.error('Error fetching employer details:', error);
    }
  };

  // Handle form submit
  const handleFormSubmit = async () => {
    const errorCount = employerValidationFN();
    
    if (errorCount === 0) {
      const city = cityName ? (cityName.charAt(0).toUpperCase() + cityName.slice(1).toLowerCase()) : '';
      
      const obj = {
        id: employerId,
        companyName: companyName ? toTitleCase1(companyName.trim()) : '',
        emailId: emailValue ? emailValue.trim() : '',
        address: addressText ? addressText.trim() : '',
        area: areaName ? areaName.trim() : '',
        city: city,
        pincode: userLocationDetails.pin_code || null,
        state: userLocationDetails.state || null,
        country: userLocationDetails.country || null,
        industry: industry,
        mobileCountryCode: '91',
        mobileNumber: mobileNum,
        phoneCountryCode: null,
        category: businessType,
        yearFounded: yearFound,
        contactPersonName: contactName ? toTitleCase(contactName) : '',
        noOfEmployees: numOfEmployees,
        websiteUrl: websiteUrl || null,
        phone: contactNum || null,
        whatsappNumber: whatsappNumber,
        regProofNumber: gstNum ? gstNum.toUpperCase() : '',
        reference: referalOption,
      };

      try {
        const response = await fetch(`${getAdminBaseUrl()}/employerRegistration`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(obj),
        });

        const data = await response.json();
        
        if (response.ok) {
          successPopup('Employer Registration Successful');
          console.log('Success:', data);
          
          // Upload company logo if exists
          if (companyLogo) {
            await uploadCompanyLogonode(employerId);
          }
          if (onSuccess) {
            onSuccess();
          }
          setTimeout(() => {
            if (onClose) {
              onClose();
            }
            // window.location.reload();
          }, 2000);
        } else {
          alert('Sorry, something went wrong');
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Sorry, something went wrong');
      }
    }
  };

  // Upload company logo to Node.js backend
  const uploadCompanyLogonode = async (empId) => {
    if (!companyLogo || !empId) return;

    const formData = new FormData();
    formData.append('file', companyLogo);

    try {
      const response = await fetch(
        `${getNodejsBaseUrl()}/updateCompanyLogo?empId=${empId}&docType=CompanyLogo`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.data && data.data.companyLogo) {
        setLogoPreview(data.data.companyLogo);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Upload failed!');
    }
  };

  // Success popup function
  const successPopup = (successText) => {
    const popupDiv = document.createElement('div');
    popupDiv.className = styles.successPopupWrp;
    popupDiv.innerHTML = `
      <style>
        #success_popup svg {
          width: 100px;
          height: 100px;
          position: absolute;
          top: 35px;
          left: 50%;
          margin-left: -50px;
        }
        svg.animate path {
          animation: dash 0.75s linear both;
          animation-delay: 0.5s;
        }
        @keyframes dash {
          0% { stroke-dashoffset: 210; }
          75% { stroke-dashoffset: -220; }
          100% { stroke-dashoffset: -205; }
        }
      </style>
      <div class="modal" id="success_popup" style="background: #42484d45 !important;">
        <div class="modal-dialog modal-dialog-centered" style="max-width: 230px; margin-left: auto; margin-right: auto;">
          <div class="modal-content" style="border-radius: 15px; height: 230px;">
            <svg viewBox="0 0 100 100" class="animate">
              <filter id="dropshadow" height="100%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="3" result="blur"/>
                <feFlood flood-color="rgba(76, 175, 80, 1)" flood-opacity="0.5" result="color"/>
                <feComposite in="color" in2="blur" operator="in" result="blur"/>
                <feMerge>
                  <feMergeNode/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
              <circle cx="50" cy="50" r="46.5" fill="none" stroke="rgba(76, 175, 80, 0.5)" stroke-width="5"/>
              <path d="M67,93 A46.5,46.5 0,1,0 7,32 L43,67 L88,19" fill="none" stroke="rgba(76, 175, 80, 1)" stroke-width="5" stroke-linecap="round" stroke-dasharray="80 1000" stroke-dashoffset="-220" style="filter:url(#dropshadow)"/>
            </svg>
            <p id="successText" style="position: absolute; text-align: center; width: 100%; top: 70%; color: #4baf4f; font-weight: 500; font-size: 23px; font-family: 'DM Sans';">${successText}</p>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(popupDiv);
    
    setTimeout(() => {
      const popup = document.querySelector(`.${styles.successPopupWrp}`);
      if (popup) {
        popup.remove();
      }
    }, 5000);
  };
  
  return (
    <div className={styles.container}>
      {/* Employer ID Popup - Commented out as in original */}
      {/* <div className="common_popupContainer" id="employerIdPopup">
        <div className="common_popup_body">
          <div className="common_popupContent">
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="col-sm-6">
                <div className="form-group">
                  <label id="EmployerLabel" htmlFor="EmployerNo">Employer Id</label>
                  <input 
                    className="form-control only_number" 
                    type="number" 
                    onKeyPress={(e) => {
                      if (e.target.value.length === 5) e.preventDefault();
                    }} 
                    id="employerId1" 
                    maxLength="10" 
                    placeholder="Enter your number"
                    value={employerId}
                    onChange={(e) => setEmployerId(e.target.value)}
                  />
                  <div className="validMsg">
                    Please enter Employer id only digit allowed
                  </div>
                </div>
              </div>
              <button type="button" onClick={() => uploadCompanyDetails(employerId)}>OK</button>
              <div className="form-group">
                <button type="submit" className="btn loginbtn text-center mt-5" id="okbutton" style={{width: '40%'}}>OK</button>
              </div>
            </form>
          </div>
        </div>
      </div> */}

      <div className={styles.CompDetlsfrm}>
        <div className={styles.cmpdtlsfrmwrp}>
          <div className={styles.cmpdtlsfrm}>
            <form onSubmit={(e) => e.preventDefault()}>
              {/* <h4 className={styles.frmtitle}>Company Details Update</h4> */}
              
              {/* Company Logo Upload - Commented out as in original */}
              {/* <div className="col-sm-6 form-group">
                <label htmlFor="myfile">Company Logo (Recommended)</label>
                <div className="row form-group">
                  <div className="col-3" style={{ zIndex: 1 }}>
                    <div className="logoSec text-left">
                      <button
                        id="companyImg_btn"
                        type="button"
                        tabIndex="1"
                        style={{
                          backgroundColor: 'inherit',
                          borderStyle: 'none',
                        }}
                        onClick={handleLogoClick}
                      >
                        <img
                          src={logoPreview}
                          width="75px"
                          height="75px"
                          style={{
                            borderRadius: '10px',
                            borderColor: '#e2e0e0',
                            borderStyle: 'solid',
                            backgroundColor: 'inherit',
                          }}
                          className="companyImg"
                          id="companyImg"
                          alt="Company Logo"
                        />
                      </button>
                      <input
                        type="file"
                        id="myfile"
                        onChange={handleLogoChange}
                        ref={fileInputRef}
                        className="d-none"
                        name="myfile"
                        accept=".jpg, .jpeg, .png"
                      />
                    </div>
                  </div>
                  <div className="col-9">
                    <div className="d-grid uploadContainer">
                      <button
                        style={{
                          margin: 'auto 10px',
                          color: '#316DC5',
                          borderStyle: 'none',
                          backgroundColor: 'inherit',
                          cursor: 'pointer',
                        }}
                        type="button"
                        id="uploadLogo"
                        tabIndex="2"
                        onClick={handleLogoClick}
                      >
                        {uploadButtonText}
                      </button>
                    </div>
                  </div>
                  <div className="validMsg fileValidate" style={{ color: 'red' }}>
                    Please upload company logo
                  </div>
                </div>
              </div> */}

              <div className={`row`}>
                <div className={`col-sm-6`}>
                  <div className={styles['form-group']}>
                    <label id="EmployerLabel" htmlFor="EmployerNo">Employer Id</label>
                    <input 
                      className={`${styles['form-control']} only_number`} 
                      type="number" 
                      onKeyPress={(e) => {
                        if (e.target.value.length === 5) e.preventDefault();
                      }} 
                      id="employerId1" 
                      maxLength="10" 
                      placeholder="Enter your number"
                      value={employerId}
                      onChange={(e) => handleInputChange(e, setEmployerId)}
                    />
                    <div className={styles.validMsg}>
                      Please enter Employer id only digit allowed
                    </div>
                  </div>
                </div>

                <div className={`col-sm-6`}>
                  <div className={styles['form-group']}>
                    <label htmlFor="industry">Industry</label>
                    <select
                      required
                      className={styles['form-control']}
                      id="industry"
                      autoFocus
                      tabIndex="3"
                      value={industry}
                      onChange={(e) => handleSelectChange(e, setIndustry)}
                    >
                      <option value="">Select Industry</option>
                      {industries.map((ind, index) => (
                        <option key={index} value={ind.industry}>
                          {ind.industry}
                        </option>
                      ))}
                    </select>
                    <div className={styles.validMsg} style={{ color: 'red' }}>
                      Please select the industry
                    </div>
                  </div>
                </div>

                <div className={`col-sm-6 ${styles['form-group']}`}>
                  <label htmlFor="business_type">Business Type</label>
                  <select
                    required
                    className={styles['form-control']}
                    id="business_type"
                    autoFocus
                    tabIndex="4"
                    value={businessType}
                    onChange={(e) => handleSelectChange(e, setBusinessType)}
                  >
                    <option value="">Select</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Staffing Services">Staffing Services</option>
                    <option value="Others">Others</option>
                  </select>
                  <p className={styles.validMsg}>Please fill out this field</p>
                </div>
              </div>

              <div className={`row`}>
                <div className={`col-sm-6`}>
                  <div className={styles['form-group']}>
                    <label htmlFor="companyname">Company Name</label>
                    <input
                      type="text"
                      name="companyname"
                      id="companyName"
                      className={styles['form-control']}
                      placeholder="Company Name"
                      tabIndex="5"
                      value={companyName}
                      onChange={(e) => {
                        const value = toTitleCase1(e.target.value);
                        setCompanyName(value);
                        if (value.trim() !== '') {
                          removeError('companyName');
                        }
                      }}
                    />
                    <div className={styles.validMsg} style={{ color: 'red' }}>
                      Please enter your company name
                    </div>
                  </div>
                </div>
                <div className={`col-sm-6`}>
                  <div className={styles['form-group']}>
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      name="ciemailty"
                      id="emailValue"
                      className={styles['form-control']}
                      placeholder="Email"
                      tabIndex="6"
                      value={emailValue}
                      onChange={(e) => handleInputChange(e, setEmailValue)}
                    />
                    {/* <div className={`${styles.validMsg} ${styles.emailError}`} style={{ color: 'red' }}>
                      Please enter your email
                    </div>
                    <div className={`${styles.emailError} ${styles['d-none']}`} style={{ color: 'red' }}>
                      Please enter valid Email Id
                    </div> */}
                     {!emailValue && (
      <div className={`${styles.validMsg} ${styles.emailError}`} style={{ color: 'red' }}>
        Please enter your email id
      </div>
    )}
    {emailValue && !validateEmail(emailValue) && (
      <div className={`${styles.validMsg} ${styles.emailError}`} style={{ color: 'red' }}>
        Please enter a valid email address
      </div>
    )}
                  </div>
                </div>
              </div>

              <div className={`row`}>
                <div className={`col-sm-6`}>
                  <div className={styles['form-group']}>
                    <div>
                      <label htmlFor="cityName">City</label>
                      <input
                        type="text"
                        list="city_list"
                        className={`${styles['form-control']} text-capitalize`}
                        placeholder="Ex: Chennai"
                        id="cityName"
                        autoComplete="off"
                        style={{ backgroundColor: 'white' }}
                        tabIndex="7"
                        name="cityName"
                        value={cityName}
                        onChange={(e) => {
                          const value = e.target.value.charAt(0).toUpperCase() + 
                                      e.target.value.slice(1);
                          setCityName(value);
                          removeError('cityName');
                        }}
                      />
                      <datalist id="city_list">
                        {cities.map((city, index) => (
                          <option key={index} data-cityId={city.id} value={city.city} />
                        ))}
                      </datalist>
                      <div className={`d-grid ${styles['justify-content-end']} pe-3`} style={{ marginTop: '-20px' }}>
                        <iconify-icon icon="il:arrow-down" style={{ fontSize: '10px' }}></iconify-icon>
                      </div>
                    </div>
                    <div className={`${styles.validMsg} mt-4 ${styles.city_err_text}`} style={{ color: 'red' }}>
                      Please enter your city/town
                    </div>
                  </div>
                </div>

                <div className={`col-sm-6 mt-4 mt-sm-0`}>
                  <div className={styles['form-group']}>
                    <label htmlFor="areaName">Area</label>
                    <input
                      type="text"
                      list="areaDt"
                      className={styles['form-control']}
                      autoComplete="off"
                      placeholder="Ex: Korattur"
                      id="areaName"
                      name="areaName"
                      maxLength="45"
                      tabIndex="8"
                      value={areaName}
                      onChange={(e) => handleInputChange(e, setAreaName)}
                    />
                    <datalist id="areaDt">
                      {areas.map((area, index) => (
                        <option key={index} value={area.area} />
                      ))}
                    </datalist>
                    <div className={styles.validMsg} style={{ color: 'red' }}>
                      Please enter your area
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles['form-group']}>
                <label htmlFor="address">Address</label>
                <textarea
                  className={styles['form-control']}
                  id="addressText"
                  name="address"
                  placeholder="Ex: No-943, 39th Street, Korattur, Chennai."
                  cols="30"
                  rows="3"
                  tabIndex="9"
                  value={addressText}
                  onChange={(e) => handleInputChange(e, setAddressText)}
                ></textarea>
                <div className={styles.validMsg} style={{ color: 'red' }}>
                  Please enter your address
                </div>
              </div>

              <div className={`row`}>
                <div className={`col-sm-6`}>
                  <div className={styles['form-group']}>
                    <label htmlFor="contactName">Contact Person Name</label>
                    <input
                      type="text"
                      name="contactName"
                      id="contactName"
                      autoFocus
                      className={`${styles['form-control']} text-capitalize ${styles.contactName}`}
                      placeholder="Contact Person Name"
                      tabIndex="12"
                      value={contactName}
                      onChange={(e) => handleInputChange(e, setContactName)}
                      onKeyDown={(e) => {
                        if (e.ctrlKey || e.altKey) {
                          e.preventDefault();
                        } else {
                          const key = e.keyCode;
                          if (!(
                            key === 8 ||
                            key === 32 ||
                            key === 13 ||
                            key === 46 ||
                            key === 9 ||
                            (key >= 35 && key <= 40) ||
                            (key >= 65 && key <= 90)
                          )) {
                            e.preventDefault();
                          }
                        }
                      }}
                    />
                    <div className={styles.validMsg} style={{ color: 'red' }}>
                      Please enter the contact person name
                    </div>
                  </div>
                </div>

                <div className={`col-sm-6`}>
                  <div className={styles['form-group']}>
                    <label htmlFor="whatapp_number">WhatsApp number</label>
                    <input
                      type="number"
                      onKeyPress={(e) => {
                        if (e.target.value.length === 10) e.preventDefault();
                      }}
                      name="whatapp_number"
                      id="whatapp_number"
                      autoFocus
                      className={`${styles['form-control']} text-capitalize`}
                      placeholder="WhatsApp Number"
                      tabIndex="13"
                      value={whatsappNumber}
                      onChange={(e) => {
                        setWhatsappNumber(e.target.value);
                        removeError('whatapp_number');
                      }}
                    />
                    <div className={styles.validMsg} style={{ color: 'red' }}>
                      Please enter the valid WhatsApp number
                    </div>
                  </div>
                </div>
              </div>

              <div className={`row`}>
                <div className={`col-sm-6`}>
                  <div className={styles['form-group']}>
                    <label htmlFor="websiteURL">Website URL (Optional)</label>
                    <input
                      type="text"
                      name="websiteURL"
                      maxLength="75"
                      id="websiteURL"
                      className={styles['form-control']}
                      placeholder="Website URL"
                      tabIndex="14"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                    />
                    <div className={styles.validMsg} style={{ color: 'red' }}>
                      Please fill out this field
                    </div>
                  </div>
                </div>

                <div className={`col-sm-6`}>
                  <div className={styles['form-group']}>
                    <label htmlFor="mobileNum">Mobile number</label>
                    <input
                      type="number"
                      onKeyPress={(e) => {
                        if (e.target.value.length === 10) e.preventDefault();
                      }}
                      name="mobileNum"
                      id="mobileNum"
                      autoFocus
                      className={`${styles['form-control']} text-capitalize`}
                      placeholder="Mobile Number"
                      tabIndex="15"
                      value={mobileNum}
                      onChange={(e) => {
                        setMobileNum(e.target.value);
                        removeError('mobileNum');
                      }}
                    />
                    <div className={styles.validMsg} style={{ color: 'red' }}>
                      Please enter the valid Mobile number
                    </div>
                  </div>
                </div>
              </div>

              <div className={`row`}>
                <div className={`col-sm-6`}>
                  <div className={styles['form-group']}>
                    <label htmlFor="numOfEmployes">No of Employees</label>
                    <select
                      required
                      className={styles['form-control']}
                      name=""
                      id="numOfEmployes"
                      tabIndex="16"
                      value={numOfEmployees}
                      onChange={(e) => handleSelectChange(e, setNumOfEmployees)}
                    >
                      <option value="">Select No of employees</option>
                      <option value="0-1">0-1</option>
                      <option value="2-10">2-10</option>
                      <option value="11-50">11-50</option>
                      <option value="51-200">51-200</option>
                      <option value="200+">200+</option>
                    </select>
                    <div className={styles.validMsg} style={{ color: 'red' }}>
                      Please select no of employees
                    </div>
                  </div>
                </div>

                <div className={`col-sm-6`}>
                  <div className={styles['form-group']}>
                    <label htmlFor="contactNum">Landline Number (Optional)</label>
                    <input
                      type="number"
                      onKeyPress={(e) => {
                        if (e.target.value.length === 10) e.preventDefault();
                      }}
                      name="contactNum"
                      id="contactNum"
                      className={styles['form-control']}
                      placeholder="Landline number"
                      tabIndex="17"
                      value={contactNum}
                      onChange={(e) => setContactNum(e.target.value)}
                    />
                    <div className={styles.validMsg} style={{ color: 'red' }}>
                      Please fill out this field
                    </div>
                  </div>
                </div>
              </div>

              <div className={`row`}>
                <div className={`col-sm-6`}>
                  <div className={styles['form-group']}>
                    <label htmlFor="yearFound">Year Founded</label>
                    <div>
                      <input
                        list="yearFounded_list"
                        type="text"
                        name="yearFound"
                        style={{ backgroundColor: 'white' }}
                        className={styles['form-control']}
                        id="yearFound"
                        placeholder="Year Founded"
                        tabIndex="18"
                        value={yearFound}
                        onChange={(e) => handleInputChange(e, setYearFound)}
                        onKeyDown={(e) => {
                          if (e.target.value.length === 4) {
                            if (!(e.keyCode === 13 || e.keyCode === 9 || e.keyCode === 8)) {
                              e.preventDefault();
                            }
                          }
                        }}
                      />
                      <datalist id="yearFounded_list">
                        {generateYearOptions().map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </datalist>
                    </div>
                    <div className={styles.validMsg} style={{ color: 'red' }}>
                      Please select the year founded
                    </div>
                  </div>
                </div>

                <div className={`col-sm-6`}>
                  <div className={styles['form-group']}>
                    <label htmlFor="gstNum">
                      GST Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="gstNum"
                      className={`${styles['form-control']} text-uppercase`}
                      maxLength="15"
                      id="gstNum"
                      placeholder="GST Number"
                      tabIndex="19"
                      value={gstNum}
                      onChange={(e) => {
                        setGstNum(e.target.value);
                        removeError('gstNum');
                      }}
                    />
                    <div className={styles.validMsg} style={{ color: 'red' }}>
                      Please enter valid GST number
                    </div>
                  </div>
                </div>
              </div>

              <div className={`row`}>
                <div className={`col-12`}>
                  <div className={styles['form-group']}>
                    <label htmlFor="referalOption">How did you hear about us (from source) ?</label>
                    <select
                      required
                      className={styles['form-control']}
                      id="referalOption"
                      placeholder="-Select-"
                      tabIndex="20"
                      value={referalOption}
                      onChange={(e) => handleSelectChange(e, setReferalOption)}
                    >
                      <option value="">-Select-</option>
                      <option value="Email">Email</option>
                      <option value="YouTube">YouTube</option>
                      <option value="Facebook">Facebook</option>
                      <option value="Instagram">Instagram</option>
                      <option value="WhatsApp">WhatsApp</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Google Search">Google Search</option>
                      <option value="Field Visit">Field Visit</option>
                      <option value="Recommended by a friend or colleague">Recommended by a friend or colleague</option>
                      <option value="Recommended by Industrial Association">Recommended by Industrial Association</option>
                      <option value="Taizo staff member">Taizo staff member</option>
                      <option value="Other">Others</option>
                    </select>
                    <div className={styles.validMsg} style={{ color: 'red' }}>
                      Please fill out this field
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'end' }}>
                <button
                  className={`btn ${styles['bg-green']} ${styles['text-white']} px-4 mt-4`}
                  type="button"
                  id="nextStep1"
                  onClick={handleFormSubmit}
                >
                  SUBMIT
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailsUpload;